import profitCenterEndpoints from '@/lib/api/endpoints/profitCenterEndpoints';
import type { ProfitCenter } from '@/lib/models/profitCenter';
import { ProfitCenterSchema } from '@/lib/models/profitCenter';
import type { ProfitCenterIntegration } from '@/lib/models/profitCenterIntegration';
import { ProfitCenterIntegrationSchema } from '@/lib/models/profitCenterIntegration';
import type { ODataResponse } from '@/lib/models/common';
import { zodValidator } from '@/lib/utils/zodValidator';

/**
 * Interfaccia per il service dei ProfitCenters
 * Wrapper che estrae automaticamente i dati dalle response Axios
 */
export interface ProfitCentersService {
	/**
	 * Recupera la lista dei centri di profitto con query OData
	 * @param query - Query string OData (es: "?$top=25&$count=true&$orderby=name asc")
	 * @returns Promise con response OData contenente array di ProfitCenter
	 */
	getProfitCenters(query: string): Promise<ODataResponse<ProfitCenter>>;

	/**
	 * Recupera un singolo centro di profitto per ID
	 * @param id - ID del centro di profitto
	 * @returns Promise con singolo ProfitCenter
	 */
	getProfitCenter(id: number): Promise<ProfitCenter>;

	/**
	 * Aggiorna un centro di profitto esistente
	 * @param id - ID del centro di profitto da aggiornare
	 * @param payload - Dati da aggiornare
	 * @returns Promise con ProfitCenter aggiornato
	 */
	updateProfitCenter(id: number, payload: ProfitCenter): Promise<ProfitCenter>;

	/**
	 * Crea un nuovo centro di profitto
	 * @param payload - Dati del nuovo centro di profitto
	 * @returns Promise con ProfitCenter creato
	 */
	postProfitCenter(payload: ProfitCenter): Promise<ProfitCenter>;

	/**
	 * Elimina un centro di profitto
	 * @param id - ID del centro di profitto da eliminare
	 * @returns Promise void
	 */
	deleteProfitCenter(id: number): Promise<void>;

	/**
	 * Recupera le integrazioni per un centro di profitto
	 * @param id - ID del centro di profitto
	 * @returns Promise con response OData contenente array di ProfitCenterIntegration
	 */
	getIntegrations(id: number): Promise<ProfitCenterIntegration[]>;
}

/**
 * Implementazione del service per i ProfitCenters
 * Estrae automaticamente la proprietà .data dalle response Axios
 */
const profitCentersService: ProfitCentersService = {
	async getProfitCenters(query: string): Promise<ODataResponse<ProfitCenter>> {
		const response = (await profitCenterEndpoints.getProfitCenters(query)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateODataResponse(response, ProfitCenterSchema, 'ProfitCenter.getAll');
		return response;
	},

	async getProfitCenter(id: number): Promise<ProfitCenter> {
		const rawData = (await profitCenterEndpoints.getProfitCenter(id)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateSingle(rawData, ProfitCenterSchema, `ProfitCenter.getById(${id})`);
		return rawData;
	},

	async updateProfitCenter(id: number, payload: ProfitCenter): Promise<ProfitCenter> {
		const rawData = (await profitCenterEndpoints.updateProfitCenter(id, payload)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateSingle(rawData, ProfitCenterSchema, `ProfitCenter.updateById(${id})`);
		return rawData;
	},

	async postProfitCenter(payload: ProfitCenter): Promise<ProfitCenter> {
		const rawData = (await profitCenterEndpoints.postProfitCenter(payload)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateSingle(rawData, ProfitCenterSchema, 'ProfitCenter.create');
		return rawData;
	},

	async deleteProfitCenter(id: number): Promise<void> {
		return (await profitCenterEndpoints.deleteProfitCenter(id)).data;
	},

	async getIntegrations(id: number): Promise<ProfitCenterIntegration[]> {
		const { data } = await profitCenterEndpoints.getIntegrations(id);
		// Validazione runtime centralizzata per le integrazioni (attiva solo in development)
		zodValidator.validateODataResponse(
			data,
			ProfitCenterIntegrationSchema,
			`ProfitCenter.getIntegrations(${id})`,
		);
		return data.value;
	},
};

export default profitCentersService;
